import type { Href } from 'expo-router';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import { useEffect, useRef } from 'react';
import { Platform, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { isDesktop, useAuth } from '@/core';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Car, Gift, Info, Person, Road, Search } from '@/ui/icons';
import { EonSubscription } from '@/ui/icons/eon-subscription';
import { ListItem } from '@/ui/list-item';

export default function NavbarDropDown({
  visible,
  onDismiss,
  style,
}: {
  visible: boolean;
  onDismiss: () => void;
  style?: any;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const pathname = usePathname();
  const local = useLocalSearchParams<{ 'skip-tutorial'?: string }>();
  const loginContainerRef = useRef(null);
  const setPartnerMode = useSettingsStore((state) => state.setPartnerMode);

  const status = useAuth.use.status();
  const signOut = useAuth.use.signOut();

  const { data: customer } = useCustomer();

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleClickOutside = (event) => {
      if (
        loginContainerRef.current &&
        !loginContainerRef.current.contains(event.target)
      ) {
        onDismiss();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [loginContainerRef]);

  const handleSwitchToPartner = async () => {
    if (customer?.partnerToken) {
      setPartnerMode(true);
      router.replace('/partner/dashboard');
      return;
    }

    router.push('/join-partner-program');
  };

  const renderPartnerListItem = (text: string) => (
    <ListItem
      icon={
        <Icon
          type="dressed"
          icon={<Car />}
          fillColor={theme.colors.icon.color}
        />
      }
      text={text}
      onPress={async () => {
        await handleSwitchToPartner();
        onDismiss();
      }}
      withBorder={false}
      style={{ borderBottomWidth: 0 }}
    />
  );

  if (!visible) return null;

  return (
    <View ref={loginContainerRef} style={[styles.loginContainer, style]}>
      {status === 'signIn' && customer ? (
        <>
          <View
            style={{
              gap: 16,
              paddingLeft: 16,
              paddingRight: 16,
              paddingTop: 8,
              paddingBottom: 8,
            }}
          >
            <View>
              <Text type="bodyRegular">{customer?.firstname}</Text>

              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                {customer?.email}
              </Text>
            </View>

            {!isDesktop() ? (
              customer?.partnerToken ? (
                <Button
                  text="Switch to Partner"
                  type="primaryExtraSmall"
                  onPress={handleSwitchToPartner}
                  style={{ width: 130 }}
                />
              ) : (
                renderPartnerListItem('Become a partner')
              )
            ) : null}
          </View>

          <View
            style={{
              backgroundColor: theme.colors.card.default.borderColor,
              height: 0.4,
              width: '100%',
            }}
          />
        </>
      ) : null}
      <View
        style={{
          gap: 8,
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 16,
          paddingBottom: 8,
        }}
      >
        <ListItem
          icon={
            <Icon
              type="dressed"
              icon={<Search />}
              fillColor={theme.colors.icon.color}
            />
          }
          text="Search"
          onPress={async () => {
            router.navigate('/');
            onDismiss();
          }}
          withBorder={false}
          style={{ borderBottomWidth: 0 }}
        />

        <ListItem
          icon={
            <Icon
              type="dressed"
              icon={<EonSubscription />}
              fillColor={theme.colors.icon.color}
            />
          }
          text="Car Subscription"
          onPress={() => {
            router.navigate('/subscriptions');
            onDismiss();
          }}
          withBorder={false}
          style={{ borderBottomWidth: 0 }}
        />
      </View>

      <View
        style={{
          backgroundColor: theme.colors.card.default.borderColor,
          height: 0.4,
          width: '100%',
        }}
      />

      {status === 'signIn' ? (
        <>
          <View
            style={{
              gap: 8,
              paddingLeft: 16,
              paddingRight: 16,
              paddingTop: 8,
              paddingBottom: 8,
            }}
          >
            <ListItem
              icon={
                <Icon
                  type="dressed"
                  icon={<Person />}
                  fillColor={theme.colors.icon.color}
                />
              }
              text="Account"
              onPress={() => {
                router.navigate('/account');
                onDismiss();
              }}
              withBorder={false}
              style={{ borderBottomWidth: 0 }}
            />

            <ListItem
              icon={
                <Icon
                  type="dressed"
                  icon={<Road />}
                  fillColor={theme.colors.icon.color}
                />
              }
              text="Trips"
              onPress={() => {
                router.navigate('/trips');
                onDismiss();
              }}
              withBorder={false}
              style={{ borderBottomWidth: 0 }}
            />
          </View>

          <View
            style={{
              backgroundColor: theme.colors.card.default.borderColor,
              height: 0.4,
              width: '100%',
            }}
          />
        </>
      ) : null}

      {status === 'signIn' ? (
        <>
          <View
            style={{
              gap: 8,
              paddingLeft: 16,
              paddingRight: 16,
              paddingTop: 8,
              paddingBottom: 8,
            }}
          >
            <ListItem
              icon={
                <Icon
                  type="dressed"
                  icon={<Gift />}
                  fillColor={theme.colors.icon.color}
                />
              }
              text="Get $30 credit"
              onPress={() => {
                router.navigate('/account/referrals');
                onDismiss();
              }}
              withBorder={false}
              style={{ borderBottomWidth: 0 }}
            />
          </View>
          <View
            style={{
              backgroundColor: theme.colors.card.default.borderColor,
              height: 0.4,
              width: '100%',
            }}
          />
        </>
      ) : null}

      <View
        style={{
          gap: 16,
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 8,
          paddingBottom: 16,
        }}
      >
        <ListItem
          icon={
            <Icon
              type="dressed"
              icon={<Info />}
              fillColor={theme.colors.icon.color}
            />
          }
          text="Support"
          onPress={async () => {
            router.navigate('/(app)/account/support-faq');
            onDismiss();
          }}
          withBorder={false}
          style={{ borderBottomWidth: 0 }}
        />

        {status === 'signIn' ? (
          <ListItem
            text="Log out"
            onPress={() => {
              setPartnerMode(false);
              signOut();
              router.replace('/search');
              onDismiss();

              Toast.show({
                text1: 'You have logged out',
                type: 'success',
              });
            }}
            withBorder={false}
            style={{ borderBottomWidth: 0 }}
          />
        ) : null}
      </View>

      {status === 'signOut' ? (
        <>
          <View
            style={{
              backgroundColor: theme.colors.card.default.borderColor,
              height: 0.4,
              width: '100%',
            }}
          />
          <View style={{ gap: 16, padding: 16 }}>
            <ListItem
              tx="navbar.loginSignup"
              onPress={() => {
                // Only resume the purchase flows here (the same returnTo targets
                // the checkout buttons use): a car booking should land on
                // /checkout and a subscription on the subscription checkout.
                // Any other origin just goes to /login.
                let loginHref = '/login';
                if (pathname?.startsWith('/car/')) {
                  loginHref = `/login?returnTo=${encodeURIComponent(
                    '/checkout'
                  )}`;
                } else if (pathname === '/subscriptions') {
                  // skip-tutorial isn't persisted, so it has to be re-appended
                  // here or the user lands back on /subscriptions without it and
                  // gets redirected into onboarding mid-checkout.
                  const returnTo =
                    local['skip-tutorial'] === '1'
                      ? '/subscriptions?checkout=true&skip-tutorial=1'
                      : '/subscriptions?checkout=true';

                  loginHref = `/login?returnTo=${encodeURIComponent(returnTo)}`;
                }

                router.push(loginHref as Href);
                onDismiss();
              }}
              withBorder={false}
              style={{ borderBottomWidth: 0 }}
            />
            {!isDesktop() ? renderPartnerListItem('Become a partner') : null}
          </View>
        </>
      ) : null}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  loginContainer: {
    position: 'absolute',
    top: 64,
    right: 0,
    width: 350,
    backgroundColor: theme.colors.background_2,
    borderRadius: 16,
    gap: 8,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    shadowColor: theme.colors.card.default.borderColor,
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.5,
    shadowRadius: 5,
    elevation: 10,
  },
}));
